import {
  Badge, Button, Card, EmptyState, ErrorState, Field, Input, LoadingState, Notice, PageHeader, Row,
  Select, Stack, Textarea, complaintTone, useApi, useContactRole, useI18n,
  type ComplaintCategory, type ComplaintResponse,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

const CATEGORIES: ComplaintCategory[] = [
  'StaffConduct', 'CareQuality', 'Safety', 'Food', 'Facilities', 'Billing', 'Communication', 'Other',
];

/**
 * Raising a concern with the nursery, and the list of concerns already raised (§4.14).
 *
 * The screen a parent reaches when something has gone wrong, which is the worst possible moment to meet a
 * form that asks them to classify their problem before it will listen. So the category has a plain "أمر آخر"
 * row and nothing but the subject and the body is required: a parent who cannot decide whether this is about
 * care or about safety must still be able to send it.
 *
 * Naming a member of staff is optional and says, in the form itself, what naming one does. A parent who is
 * afraid of what happens to their child if they complain about a teacher is the reason this module exists,
 * and leaving them to guess whether the teacher will be told would defeat it (FR-CMP-02).
 */
export function Complaints({ childId }: { childId: number }) {
  const api = useApi();
  const { t, formatDate } = useI18n();
  const contactRole = useContactRole();
  const queryClient = useQueryClient();

  const [category, setCategory] = useState<ComplaintCategory>('Other');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [about, setAbout] = useState('');
  const [open, setOpen] = useState(false);

  const complaints = useQuery({
    queryKey: ['complaints', 'mine'],
    queryFn: () => api.complaints.list({ pageSize: 50 }),
  });

  // The same list a message may be sent to: this child's teachers and the administration. Reusing it means
  // the names a parent is offered here are exactly the people they already know they can reach.
  const contacts = useQuery({
    queryKey: ['conversations', 'contacts'],
    queryFn: () => api.conversations.contacts(),
  });

  const raise = useMutation({
    mutationFn: () => api.complaints.create({
      category,
      subject: subject.trim(),
      body: body.trim(),
      childId,
      aboutMembershipId: about === '' ? null : Number(about),
    }),
    onSuccess: async () => {
      setSubject('');
      setBody('');
      setAbout('');
      setCategory('Other');
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['complaints'] });
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (subject.trim() && body.trim()) raise.mutate();
  };

  const mine = complaints.data?.items ?? [];

  return (
    <Stack>
      <PageHeader
        title={t('complaints.title')}
        actions={
          <Button onClick={() => setOpen((was) => !was)}>
            {open ? t('action.cancel') : t('complaints.raise')}
          </Button>
        }
      />

      {raise.isSuccess && <Notice tone="success">{t('complaints.sent')}</Notice>}

      {open && (
        <Card title={t('complaints.raise')}>
          <form onSubmit={submit}>
            <Stack>
              {raise.isError && <ErrorState error={raise.error} />}

              <Notice tone="info">{t('complaints.privacy')}</Notice>

              <Field label={t('complaint.category')} required>
                {(props) => (
                  <Select
                    {...props}
                    value={category}
                    onChange={(event) => setCategory(event.target.value as ComplaintCategory)}
                  >
                    {CATEGORIES.map((option) => (
                      <option key={option} value={option}>{t(`complaint.category.${option}`)}</option>
                    ))}
                  </Select>
                )}
              </Field>

              <Field label={t('complaints.subject')} required hint={t('complaints.subject.hint')}>
                {(props) => (
                  <Input
                    {...props}
                    value={subject}
                    maxLength={256}
                    onChange={(event) => setSubject(event.target.value)}
                  />
                )}
              </Field>

              <Field label={t('complaints.body')} required hint={t('complaints.body.hint')}>
                {(props) => (
                  <Textarea
                    {...props}
                    rows={6}
                    value={body}
                    onChange={(event) => setBody(event.target.value)}
                  />
                )}
              </Field>

              {/* Optional, and the hint says what naming someone does — which is the question a parent is
                  actually asking themselves at this point in the form. */}
              <Field label={t('complaints.about')} hint={t('complaints.about.hint')}>
                {(props) => (
                  <Select {...props} value={about} onChange={(event) => setAbout(event.target.value)}>
                    <option value="">{t('complaints.about.none')}</option>
                    {(contacts.data ?? []).map((contact) => (
                      <option key={contact.membershipId} value={contact.membershipId}>
                        {contact.name ?? '—'}{contactRole(contact) ? ` · ${contactRole(contact)}` : ''}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              <Row>
                <Button type="submit" busy={raise.isPending} busyLabel={t('action.saving')}>
                  {t('complaints.send')}
                </Button>
              </Row>
            </Stack>
          </form>
        </Card>
      )}

      {complaints.isPending && <LoadingState />}
      {complaints.isError && (
        <ErrorState error={complaints.error} onRetry={() => void complaints.refetch()} />
      )}

      {complaints.isSuccess && mine.length === 0 && (
        <EmptyState icon="🗒️" title={t('complaints.none')} body={t('complaints.none.hint')} />
      )}

      {mine.map((complaint) => (
        <ComplaintCard key={complaint.id} complaint={complaint} formatDate={formatDate} t={t} />
      ))}
    </Stack>
  );
}

function ComplaintCard({
  complaint, formatDate, t,
}: {
  complaint: ComplaintResponse;
  formatDate: (value: string) => string;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  return (
    <Card>
      <Stack>
        <Row>
          <Badge tone={complaintTone(complaint.status)}>
            {t(`complaint.status.${complaint.status}`)}
          </Badge>
          <span className="dg-muted">{complaint.reference}</span>
          <span className="dg-muted">{formatDate(complaint.createdAt)}</span>
        </Row>

        <Link to={`${complaint.id}`}>
          <strong>{complaint.subject}</strong>
        </Link>

        <span className="dg-muted">{t(`complaint.category.${complaint.category}`)}</span>

        {/* The outcome, on the list, because it is the one thing the family opened the app to read. */}
        {complaint.resolutionSummary != null && (
          <Notice tone="success">{complaint.resolutionSummary}</Notice>
        )}
      </Stack>
    </Card>
  );
}
